import type { RelayFrom, RelayManifest } from '@webfile/protocol'
import { SignalingError } from '../signaling/client'

export interface RelayUploadTarget {
  id: string
  url: string
  key: string
  expiresAt: number
}

/** Participant identity used to authorize data-plane writes (upload/register/delete). */
export interface RelayAuth {
  clientId: string
  role: 'host' | 'guest'
}

function authHeaders(auth: RelayAuth): Record<string, string> {
  return { 'X-Client-Id': auth.clientId, 'X-Role': auth.role }
}

/** A local file mirrored after upload/download so the peer can fetch it. */
export interface RelayTransferItem {
  id: string
  name: string
  size: number
  type: string
  from: RelayFrom
  at: string
  /** Local transfer status. */
  status: 'pending' | 'uploading' | 'downloading' | 'done' | 'error' | 'rejected'
  transferred: number
  error?: string
  /** Object URL of the fully received Blob (download complete). */
  blobUrl?: string
}

const DEFAULT_TIMEOUT_MS = 20_000

function apiBase(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) {
    return String(import.meta.env.VITE_API_BASE).replace(/\/$/, '')
  }
  return ''
}

/**
 * HTTP client for the Blob relay data plane (upload/download + manifest).
 */
export class RelayClient {
  private readonly base: string
  private readonly fetchImpl: typeof fetch

  constructor(baseUrl = apiBase(), fetchImpl: typeof fetch = fetch.bind(globalThis)) {
    this.base = baseUrl.replace(/\/$/, '')
    this.fetchImpl = fetchImpl
  }

  private url(path: string): string {
    return `${this.base}${path}`
  }

  private async req<T>(
    path: string,
    init: RequestInit,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  ): Promise<T> {
    const ctrl = new AbortController()
    const t = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      const res = await this.fetchImpl(this.url(path), { ...init, signal: ctrl.signal })
      const text = await res.text()
      let body: unknown = null
      if (text) {
        try {
          body = JSON.parse(text)
        } catch {
          body = { error: 'invalid_response', message: text.slice(0, 200) }
        }
      }
      if (!res.ok) {
        const err = (body || {}) as { error?: string; message?: string }
        throw new SignalingError(
          res.status,
          err.error || 'http_error',
          err.message || res.statusText || 'Request failed',
        )
      }
      return body as T
    } finally {
      clearTimeout(t)
    }
  }

  /** Mint a pre-signed PUT URL for a direct client-to-Blob upload. */
  uploadUrl(code: string, file: { name: string; size: number }, auth: RelayAuth): Promise<RelayUploadTarget> {
    return this.req<RelayUploadTarget>('/api/relay/upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders(auth) },
      body: JSON.stringify({ code, name: file.name, size: file.size }),
    })
  }

  /** Record an uploaded file in the room manifest. */
  register(code: string, file: RelayTransferItem, auth: RelayAuth): Promise<{ ok: true; rev: number }> {
    return this.req<{ ok: true; rev: number }>('/api/relay/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders(auth) },
      body: JSON.stringify({ code, file }),
    })
  }

  /** Fetch the room's file manifest. */
  manifest(code: string): Promise<RelayManifest> {
    return this.req<RelayManifest>(`/api/relay/manifest?code=${encodeURIComponent(code)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
  }

  /** Absolute GET URL that streams a file's bytes back for download. */
  downloadUrl(code: string, id: string, name: string): string {
    const qs = new URLSearchParams({ code, id, name })
    return `${this.base}/api/relay/download?${qs.toString()}`
  }

  /** Delete a file blob + its manifest entry (receiver cleanup / aborted upload). */
  delete(code: string, id: string, auth: RelayAuth): Promise<{ ok: true; removed: number }> {
    return this.req<{ ok: true; removed: number }>('/api/relay/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders(auth) },
      body: JSON.stringify({ code, id }),
    })
  }

  /** Best-effort delete of all room blobs. */
  cleanup(code: string): Promise<{ ok: true; removed: number }> {
    return this.req<{ ok: true; removed: number }>('/api/relay/cleanup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code }),
    })
  }
}

/** Stream a GET response into a Blob with download progress reporting. */
export async function streamDownload(
  url: string,
  onProgress?: (received: number, total: number) => void,
): Promise<Blob> {
  let res = await fetch(url)
  // Eventual reads may 404 briefly right after upload — retry a few times.
  for (let attempt = 0; res.status === 404 && attempt < 3; attempt++) {
    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
    res = await fetch(url)
  }
  if (res.status === 404) throw new SignalingError(404, 'not_found', '文件尚未就绪，稍后重试')
  if (!res.ok) throw new SignalingError(res.status, 'http_error', `下载失败 (${res.status})`)
  const total = Number(res.headers.get('Content-Length')) || 0
  if (!res.body) return new Blob([await res.arrayBuffer()], { type: 'application/octet-stream' })
  const reader = res.body.getReader()
  const parts: BlobPart[] = []
  let received = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    const copy = new Uint8Array(value.byteLength)
    copy.set(value)
    parts.push(copy.buffer as ArrayBuffer)
    received += value.byteLength
    onProgress?.(received, total || received)
  }
  return new Blob(parts, { type: 'application/octet-stream' })
}

/** PUT a Blob to a pre-signed URL with upload progress reporting. */
export function xhrUpload(
  url: string,
  blob: Blob,
  onProgress?: (sent: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`上传失败 (${xhr.status})`))
    }
    xhr.onerror = () => reject(new Error('上传失败：网络错误'))
    xhr.send(blob)
  })
}

/** Auto-save a Blob URL to the browser's downloads folder (no manual button). */
export function triggerBlobDownload(url: string, name: string): void {
  const a = document.createElement('a')
  a.href = url
  a.download = name || 'download'
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}